import * as t from "io-ts"
import { IsPlain, NotPlain } from "../../crypto";
import * as crypto from "../../json/crypto"
import { ApiKeypair, PlainSecretSubmissionDataKey, PublicApiKey, PublicSubmissionDataKey, SecretApiKey, SubmissionDataKeypair } from "../../keys";
import { UnexpectedValueError } from "../../../unexpected-value-error";
import { ComponentId, PageId } from "../../ids";
import { PageType } from "../page-type";
import { FormType } from "../../../../components/lib/form-data";

export interface PageOutput {
    id: t.OutputOf<typeof PageId>,
    type: t.OutputOf<typeof PageType>,
}
export interface Page {
    id: PageId,
    type: PageType,
}
const Page: t.Type<Page, PageOutput> = t.type({
    id: PageId, 
    type: PageType,
});

export interface AnswerIdBrand {
    readonly AnswerId: unique symbol;
}
export const AnswerId = t.brand(t.string,
    (n): n is t.Branded<string, AnswerIdBrand> => true,
    "AnswerId");
export type AnswerId = t.TypeOf<typeof AnswerId>;

export function answerIdFromComponentId(componentId: ComponentId) : AnswerId {
    return UnexpectedValueError.throwIfLeft("answerIdFromComponentId",
        AnswerId.decode(
            ComponentId.encode(componentId)));
}

interface AnswerBaseChoiceOutput {
    readonly id: string;
    readonly label: string;
    readonly value?: crypto.PlainValueOutput | crypto.EncryptedValueOutput;
}
interface AnswerBaseChoice extends AnswerBaseChoiceOutput {
    readonly id: AnswerId;
    readonly label: string;
    readonly value?: crypto.PlainValue | crypto.EncryptedValue;
}

interface AnswerBaseChoicesOutput {
    readonly [key: string]: AnswerBaseChoiceOutput;
}
interface AnswerBaseChoices extends AnswerBaseChoicesOutput {
    readonly [key: string]: AnswerBaseChoice;
}

interface AnswerBaseOutput {
    readonly type: string,
    readonly title: string,
    readonly encrypt: boolean;
    readonly description: string,
    readonly plain?: boolean;
    readonly page: {
        id: string;
        type: string;
    };
    readonly data: AnswerBaseChoicesOutput;
}
interface AnswerBase extends AnswerBaseOutput {
    readonly type: FormType;
    readonly plain: boolean;
    readonly page: {
        id: PageId;
        type: PageType;
    };
    readonly data: AnswerBaseChoices;
}

export const PlainAnswerChoice = t.readonly(t.intersection([
    t.type({
        id: AnswerId,
        label: t.string
    }),
    t.partial({
        value: crypto.PlainValue
    })]));
export type PlainAnswerChoice = t.TypeOf<typeof PlainAnswerChoice>
export type PlainAnswerChoiceOutput = typeof PlainAnswerChoice._O

export const PlainAnswerChoices = t.readonly(t.record(t.string, PlainAnswerChoice));
export type PlainAnswerChoices = t.TypeOf<typeof PlainAnswerChoices>
export type PlainAnswerChoicesOutput = typeof PlainAnswerChoices._O

export interface PlainAnswerOutput extends AnswerBaseOutput {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly [key: string]: any;
    readonly encrypt: false;
    readonly plain: undefined;
    readonly data: PlainAnswerChoicesOutput;
}
export interface PlainAnswer extends AnswerBase {
    readonly encrypt: false;
    readonly plain: IsPlain;
    readonly data: PlainAnswerChoices;
}
const PlainAnswer: t.Type<PlainAnswer, PlainAnswerOutput> = t.type({
    encrypt: t.literal(false),
    plain: IsPlain,
    type: FormType,
    title: t.string,
    description: t.string,
    page: Page,
    data: PlainAnswerChoices
});

// Die Antworten des eingereichten Formulars werden mit einem generierten Schlüssel verschlüsselt.
// Dieser generierte Schlüssel wird auf unterschiedlichen Wegen unter folgenden Bereichen verschlüsselt:
// Cookie (Api-Schlüssel), Link (Link-Schlüssel), Datenspeicher (User-Schlüssel)
export const StorageEncryptedAnswerChoiceLabel = t.readonly(t.intersection([
    t.type({
        id: AnswerId,
        label: crypto.EncryptedString,
    }),
    t.partial({
        value: t.undefined
    })]));
export const StorageEncryptedAnswerChoiceValue = t.readonly(t.intersection([
    t.type({
        id: AnswerId,
        label: t.string,
    }),
    t.partial({
        value: t.union([t.undefined, crypto.EncryptedValue]),
    })
]));
export const StorageEncryptedAnswerChoice = t.union([
    StorageEncryptedAnswerChoiceLabel,
    StorageEncryptedAnswerChoiceValue
]);
export type StorageEncryptedAnswerChoice = t.TypeOf<typeof StorageEncryptedAnswerChoice>
export type StorageEncryptedAnswerChoiceOutput = typeof StorageEncryptedAnswerChoice._O

export const StorageEncryptedAnswerChoices = t.readonly(t.record(t.string, StorageEncryptedAnswerChoice));
export type StorageEncryptedAnswerChoices = t.TypeOf<typeof StorageEncryptedAnswerChoices>
export type StorageEncryptedAnswerChoicesOutput = typeof StorageEncryptedAnswerChoices._O

interface StorageEncryptedAnswerBrand {
    readonly StorageEncryptedAnswer: unique symbol;
}
export interface StorageEncryptedAnswerOutput extends AnswerBaseOutput {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly [key: string]: any;
    encrypt: true;
    plain: undefined;
    data: StorageEncryptedAnswerChoicesOutput;
}
interface StorageEncryptedAnswerData extends AnswerBase {
    encrypt: true;
    plain: NotPlain;
    data: StorageEncryptedAnswerChoices;
}
const StorageEncryptedAnswerData: t.Type<StorageEncryptedAnswerData, StorageEncryptedAnswerOutput> = t.type({
    encrypt: t.literal(true),
    plain: NotPlain,
    type: FormType,
    title: t.string,
    description: t.string,
    page: Page,
    data: StorageEncryptedAnswerChoices,
});
export const StorageEncryptedAnswer = t.brand(StorageEncryptedAnswerData,
    (n): n is t.Branded<StorageEncryptedAnswerData, StorageEncryptedAnswerBrand> => true,
    "StorageEncryptedAnswer");
export type StorageEncryptedAnswer = t.TypeOf<typeof StorageEncryptedAnswer>;

// Beim Ausfüllen des Formulars werden die Antworten mit dem Api-Schlüssel verschlüsselt.
// Der Schlüssel für die Submission wird erst bei Empfang erzeugt.
export const TransferEncryptedAnswerChoiceLabel = t.readonly(t.intersection([
    t.type({
        id: AnswerId,
        label: crypto.EncryptedString,
    }),
    t.partial({
        value: t.undefined
    })]));
export const TransferEncryptedAnswerChoiceValue = t.readonly(t.intersection([
    t.type({
        id: AnswerId,
        label: t.string,
    }),
    t.partial({
        value: t.union([t.undefined, crypto.EncryptedValue]),
    })
]));
export const TransferEncryptedAnswerChoice = t.union([
    TransferEncryptedAnswerChoiceLabel,
    TransferEncryptedAnswerChoiceValue
]);
export type TransferEncryptedAnswerChoice = t.TypeOf<typeof TransferEncryptedAnswerChoice>
export type TransferEncryptedAnswerChoiceOutput = typeof TransferEncryptedAnswerChoice._O

export const TransferEncryptedAnswerChoices = t.readonly(t.record(t.string, TransferEncryptedAnswerChoice));
export type TransferEncryptedAnswerChoices = t.TypeOf<typeof TransferEncryptedAnswerChoices>
export type TransferEncryptedAnswerChoicesOutput = typeof TransferEncryptedAnswerChoices._O

interface TransferEncryptedAnswerBrand {
    readonly TransferEncryptedAnswer: unique symbol;
}
export interface TransferEncryptedAnswerOutput extends AnswerBaseOutput {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly [key: string]: any;
    encrypt: true;
    plain: undefined;
    data: TransferEncryptedAnswerChoicesOutput;
}
interface TransferEncryptedAnswerData extends AnswerBase {
    encrypt: true;
    plain: NotPlain;
    data: TransferEncryptedAnswerChoices;
}
const TransferEncryptedAnswerData: t.Type<TransferEncryptedAnswerData, TransferEncryptedAnswerOutput> = t.type({
    encrypt: t.literal(true),
    plain: NotPlain,
    type: FormType,
    title: t.string,
    description: t.string,
    page: Page,
    data: TransferEncryptedAnswerChoices,
});
export const TransferEncryptedAnswer = t.brand(TransferEncryptedAnswerData,
    (n): n is t.Branded<TransferEncryptedAnswerData, TransferEncryptedAnswerBrand> => true,
    "TransferEncryptedAnswer");
export type TransferEncryptedAnswer = t.TypeOf<typeof TransferEncryptedAnswer>;

const DecryptedAnswerChoice = t.readonly(t.intersection([
    t.type({
        id: AnswerId,
        label: t.string
    }),
    t.partial({
        value: crypto.PlainValue
    })]));
export type DecryptedAnswerChoice = t.TypeOf<typeof DecryptedAnswerChoice>
export type DecryptedAnswerChoiceOutput = typeof DecryptedAnswerChoice._O

const DecryptedAnswerChoices = t.readonly(t.record(t.string, DecryptedAnswerChoice));
export type DecryptedAnswerChoices = t.TypeOf<typeof DecryptedAnswerChoices>
export type DecryptedAnswerChoicesOutput = typeof DecryptedAnswerChoices._O

export interface DecryptedAnswerOutput extends AnswerBaseOutput {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly [key: string]: any;
    encrypt: true;
    plain: true;
    data: DecryptedAnswerChoicesOutput;
}
export interface DecryptedAnswer extends AnswerBase {
    encrypt: true;
    plain: true;
    data: DecryptedAnswerChoices;
}
const DecryptedAnswer: t.Type<DecryptedAnswer, DecryptedAnswerOutput> = t.type({
    encrypt: t.literal(true),
    plain: t.literal(true),
    type: FormType,
    title: t.string,
    description: t.string,
    page: Page,
    data: DecryptedAnswerChoices,
});

async function encryptAnswerCore(publicKey: PublicApiKey | PublicSubmissionDataKey, input: DecryptedAnswer): Promise<TransferEncryptedAnswer | StorageEncryptedAnswer> {
    const inputData = input.data;
    const encryptedData: { [key: string]: TransferEncryptedAnswerChoice | StorageEncryptedAnswerChoice } = {};
    for (const dataId in inputData) {
        const dataItem = inputData[dataId] as typeof inputData[typeof dataId];
        if (dataItem.value) {
            encryptedData[dataId] = {
                id: dataItem.id,
                label: dataItem.label,
                value: dataItem.value ? await crypto.encrypt(publicKey, dataItem.value) : undefined
            };
        } else {
            const label = dataItem.label as (typeof dataItem.label extends string ? crypto.PlainString : unknown);
            encryptedData[dataId] = {
                id: dataItem.id,
                label: await crypto.encrypt(publicKey, label),
                value: undefined
            };
        }
    }
    return {
        ...input,
        plain: false,
        data: encryptedData
    } as TransferEncryptedAnswer | StorageEncryptedAnswer;
}

export async function encryptAnswer(publicKey: PublicApiKey, input: DecryptedAnswer): Promise<TransferEncryptedAnswer>
export async function encryptAnswer(publicKey: PublicSubmissionDataKey, input: DecryptedAnswer): Promise<StorageEncryptedAnswer>
export async function encryptAnswer(publicKey: PublicApiKey | PublicSubmissionDataKey, input: DecryptedAnswer): Promise<TransferEncryptedAnswer | StorageEncryptedAnswer> {
    return encryptAnswerCore(publicKey, input);
}

async function decryptAnswerCore(publicKey: PublicSubmissionDataKey | PublicApiKey, secretKey: PlainSecretSubmissionDataKey | SecretApiKey, input: TransferEncryptedAnswer | StorageEncryptedAnswer): Promise<DecryptedAnswer> {
    const inputData = input.data;
    const decryptedData: { [key: string]: DecryptedAnswerChoice } = {};
    for (const dataId in inputData) {
        const dataItem = inputData[dataId] as typeof inputData[typeof dataId];
        if (dataItem.value) {
            decryptedData[dataId] = {
                id: dataItem.id,
                label: dataItem.label,
                value: dataItem.value && await crypto.decrypt(publicKey, secretKey, dataItem.value)
            };
        } else {
            const label = dataItem.label as (typeof dataItem.label extends string ? crypto.EncryptedString : unknown);
            decryptedData[dataId] = {
                id: dataItem.id,
                label: dataItem.label && await crypto.decrypt(publicKey, secretKey, label),
                value: undefined
            };
        }
    }
    return {
        ...input,
        plain: true,
        data: decryptedData
    };
}

export async function decryptAnswer(publicKey: PublicApiKey, secretKey: SecretApiKey, input: TransferEncryptedAnswer): Promise<DecryptedAnswer>
export async function decryptAnswer(publicKey: PublicSubmissionDataKey, secretKey: PlainSecretSubmissionDataKey, input: StorageEncryptedAnswer): Promise<DecryptedAnswer>
export async function decryptAnswer(publicKey: PublicSubmissionDataKey | PublicApiKey, secretKey: PlainSecretSubmissionDataKey | SecretApiKey, input: TransferEncryptedAnswer | StorageEncryptedAnswer): Promise<DecryptedAnswer> {
    return decryptAnswerCore(publicKey, secretKey, input);
}

export const StorageAnswer = t.union([
    PlainAnswer,
    StorageEncryptedAnswer
]);
export type StorageAnswer = t.TypeOf<typeof StorageAnswer>;

export const StorageAnswers = t.record(t.string, StorageAnswer);
export type StorageAnswers = t.TypeOf<typeof StorageAnswers>;

export const TransferAnswer = t.union([
    PlainAnswer,
    TransferEncryptedAnswer
]);
export type TransferAnswer = t.TypeOf<typeof TransferAnswer>;

export const TransferAnswers = t.record(t.string, TransferAnswer);
export type TransferAnswers = t.TypeOf<typeof TransferAnswers>;

export type PlainAnswers = Record<string, PlainAnswer>
export type StorageEncryptedAnswers = Record<string, StorageEncryptedAnswer>
export type TransferEncryptedAnswers = Record<string, TransferEncryptedAnswer>
export type DecryptedAnswers = Record<string, DecryptedAnswer>

const UnencryptedAnswer = t.union([
    PlainAnswer,
    DecryptedAnswer
]);
export type UnencryptedAnswer = t.TypeOf<typeof UnencryptedAnswer>;
export const UnencryptedAnswers = t.record(t.string, UnencryptedAnswer);
export type UnencryptedAnswers = t.TypeOf<typeof UnencryptedAnswers>;
export type UnencryptedAnswersOutput = t.OutputOf<typeof UnencryptedAnswers>;

export async function encryptAnswers(publicKey: PublicSubmissionDataKey, input: PlainAnswers): Promise<PlainAnswers>
export async function encryptAnswers(publicKey: PublicSubmissionDataKey, input: DecryptedAnswers): Promise<StorageEncryptedAnswers>
export async function encryptAnswers(publicKey: PublicSubmissionDataKey, input: UnencryptedAnswers): Promise<StorageAnswers>
export async function encryptAnswers(publicKey: PublicApiKey, input: PlainAnswers): Promise<PlainAnswers>
export async function encryptAnswers(publicKey: PublicApiKey, input: DecryptedAnswers): Promise<TransferEncryptedAnswers>
export async function encryptAnswers(publicKey: PublicApiKey, input: UnencryptedAnswers): Promise<TransferAnswers>
export async function encryptAnswers(publicKey: PublicSubmissionDataKey | PublicApiKey, input: UnencryptedAnswers): Promise<StorageAnswers | TransferAnswers> {
    const encryptedAnswers: { [key: string]: StorageAnswer | TransferAnswer } = {};
    for (const id in input) {
        const unencrypted = input[id] as typeof input[typeof id];
        encryptedAnswers[id] =
            !unencrypted.encrypt ? unencrypted
                // TODO: Wie kann ich hier encryptAnswer ohne Compilerfehler aufrufen?
                : await encryptAnswerCore(publicKey, unencrypted);
    }
    return encryptedAnswers as StorageAnswers | TransferAnswers;
}

export async function decryptAnswers({ publicKey, secretKey }: SubmissionDataKeypair, input: PlainAnswers): Promise<PlainAnswers>
export async function decryptAnswers({ publicKey, secretKey }: SubmissionDataKeypair, input: StorageEncryptedAnswers): Promise<DecryptedAnswers>
export async function decryptAnswers({ publicKey, secretKey }: SubmissionDataKeypair, input: StorageAnswers): Promise<UnencryptedAnswers>
export async function decryptAnswers({ publicKey, secretKey }: ApiKeypair, input: PlainAnswers): Promise<PlainAnswers>
export async function decryptAnswers({ publicKey, secretKey }: ApiKeypair, input: TransferEncryptedAnswers): Promise<DecryptedAnswers>
export async function decryptAnswers({ publicKey, secretKey }: ApiKeypair, input: TransferAnswers): Promise<UnencryptedAnswers>
export async function decryptAnswers({ publicKey, secretKey }: SubmissionDataKeypair | ApiKeypair, input: StorageAnswers | TransferAnswers): Promise<UnencryptedAnswers> {
    const decryptedAnswers: { [key: string]: UnencryptedAnswer } = {};
    for (const id in input) {
        const undecrypted = input[id] as typeof input[typeof id];
        decryptedAnswers[id] =
            !undecrypted.encrypt ? undecrypted
                // TODO: Wie kann ich hier encryptAnswer ohne Compilerfehler aufrufen?
                : await decryptAnswerCore(publicKey, secretKey, undecrypted);
    }
    return decryptedAnswers;
}
